import { useState } from 'react';
import { Building2, Star, Plus, Pencil, Trash2, Trophy, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import type { PersonDetail } from '../../types';
import { EditOrganizationLinkModal } from './EditOrganizationLinkModal';
import toast from 'react-hot-toast';
import { formatSportLabel } from '../../lib/sportLabel';
import { DirectionsButton } from '../directions';
import { hasDirectionsDestination } from '../../lib/directions';

interface OrganizationLink {
  id: string;
  role: string | null;
  isPrimary: boolean;
  organization: {
    id: string;
    name: string;
    city: string | null;
    state: string | null;
    type: string;
  };
  sport: {
    id: string;
    name: string;
    gender: string;
    season: string;
  } | null;
  team: {
    id: string;
    level: string;
    blastTeamId: string | null;
  } | null;
}

interface PersonOrganizationsProps {
  person: PersonDetail;
  onLinkOrganization?: () => void;
  onLinksChanged?: () => void; // Called after edit/delete to refresh person data
  canEdit?: boolean; // Whether user can edit (based on role/assignment)
}

/**
 * Display and manage linked organizations
 * Shows org name, city/state, role, sport, and primary indicator
 * Supports edit and delete when canEdit is true
 */
export const PersonOrganizations = ({
  person,
  onLinkOrganization,
  onLinksChanged,
  canEdit = true,
}: PersonOrganizationsProps) => {
  const navigate = useNavigate();
  const organizations = person.organizations || [];

  // Edit modal state
  const [editingLink, setEditingLink] = useState<OrganizationLink | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Delete state
  const [deletingLinkId, setDeletingLinkId] = useState<string | null>(null);

  // Handle edit click
  const handleEditClick = (e: React.MouseEvent, link: OrganizationLink) => {
    e.stopPropagation(); // Prevent navigation
    setEditingLink(link);
    setIsEditModalOpen(true);
  };

  // Handle edit success
  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    setEditingLink(null);
    onLinksChanged?.();
  };

  // Handle delete click
  const handleDeleteClick = async (e: React.MouseEvent, link: OrganizationLink) => {
    e.stopPropagation(); // Prevent navigation

    if (!confirm(`Remove link to ${link.organization.name}?`)) {
      return;
    }

    setDeletingLinkId(link.id);

    try {
      await api.delete(`/api/people/${person.id}/organizations/${link.id}`);
      toast.success(`Unlinked from ${link.organization.name}`);
      onLinksChanged?.();
    } catch (err: any) {
      console.error('Failed to delete link:', err);
      toast.error(err.response?.data?.message || 'Failed to remove link');
    } finally {
      setDeletingLinkId(null);
    }
  };

  // Handle org card click (navigate to org detail)
  const handleOrgClick = (orgId: string) => {
    navigate(`/organizations/${orgId}`);
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Organizations & Teams
        </h3>
        {canEdit && (
          <button
            onClick={onLinkOrganization}
            disabled={!onLinkOrganization}
            className="btn-secondary text-sm py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            title={onLinkOrganization ? 'Link to an organization' : 'Coming soon'}
          >
            <Plus className="h-4 w-4 mr-1" />
            Link Organization
          </button>
        )}
      </div>

      {organizations.length === 0 ? (
        <div className="text-center py-8">
          <Building2 className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">No organizations linked</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Link this person to schools or organizations they work with
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {organizations.map((link) => (
            <div
              key={link.id}
              className="flex items-start p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              {/* Clickable area for navigation */}
              <div
                onClick={() => handleOrgClick(link.organization.id)}
                className="flex items-start flex-1 min-w-0 cursor-pointer"
              >
                {/* Icon */}
                <div className="flex-shrink-0 h-10 w-10 bg-white dark:bg-gray-700 rounded-lg flex items-center justify-center border border-gray-200 dark:border-gray-600">
                  <Building2 className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </div>

                {/* Info */}
                <div className="ml-4 flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                      {link.organization.name}
                    </span>
                    {link.isPrimary && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                        <Star className="h-3 w-3 mr-1 fill-current" />
                        Primary
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {[link.organization.city, link.organization.state]
                      .filter(Boolean)
                      .join(', ') || 'No location'}
                  </div>
                  {hasDirectionsDestination(link.organization) && (
                    <div className="mt-1">
                      <DirectionsButton destination={link.organization} variant="inline" />
                    </div>
                  )}
                  {link.role && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                      {link.role}
                    </div>
                  )}
                  {link.sport && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Trophy className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {formatSportLabel(link.sport.gender, link.sport.name)}
                      </span>
                      {link.team && (
                        <>
                          <span className="text-gray-300 dark:text-gray-600">›</span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {link.team.level}
                          </span>
                          {link.team.blastTeamId && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                              <Zap className="h-3 w-3 mr-0.5" />
                              Blast
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Organization type badge */}
                <div className="flex-shrink-0 ml-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                    {link.organization.type === 'SCHOOL' ? 'School' : 'Club'}
                  </span>
                </div>
              </div>

              {/* Action buttons */}
              {canEdit && (
                <div className="flex-shrink-0 ml-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => handleEditClick(e, link)}
                    className="p-1.5 text-gray-400 hover:text-blast-600 hover:bg-white dark:hover:bg-gray-700 rounded transition-colors"
                    title="Edit link"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => handleDeleteClick(e, link)}
                    disabled={deletingLinkId === link.id}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-white dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                    title="Remove link"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <EditOrganizationLinkModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingLink(null);
        }}
        personId={person.id}
        link={editingLink}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};
